// One day, opened in place under its own week.
//
// This replaced a modal, and the reason is that a day only makes sense next to
// the days around it. A sheet over the top hid the very thing you were deciding
// against — "is Thursday busier than Wednesday" can't be answered by a dialog
// covering Wednesday.
//
// It shows every trainer on the calendar, not just the focused one, because
// that's what the overlay was for: the row of dots said somebody else was
// available, and this is where you find out who and when.
//
// Your own row is always there, even with your chip switched off. Opening a day
// and not finding yourself in it is disorienting — and it's your row that
// carries the pencil, because your day is the one you can change.
//
// The panel is for reading, and the two things it can change are things that
// have nowhere else to be changed. The pencil edits the day's hours. A slot can
// be blocked — "Farrier at 5" — which the pattern builder cannot express,
// because it is not a change to the pattern: next Tuesday is fine.
//
// Both are deliberately quiet. A panel you scan beats a panel you navigate, so
// the block action sits at low contrast until you go looking for it, the same
// way the roster's add-a-shift target does.
//
// The slots come from GET /slots, the same endpoint the client booking page
// calls. So the times listed here are the times a client can actually see and
// book — not a separate rendering of the schedule that agrees with it today.

import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { IconButton, PencilIcon } from '../ui/icons';
import { fmtDate } from '../../lib/dates';
import DayTimeline from './DayTimeline';

function fmtTime(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

export default function DayDetail({
  date,
  trainers = [],
  primaryTrainerId,
  dotFor,
  isOff,
  onClose,
  onEditHours,
  // Slots the calendar is showing for this day that aren't saved yet, from a
  // pattern being built above. The panel reads storage, so without this it
  // flatly contradicts the cell it just dropped out of.
  proposedSlots = 0,
  busy = false,
  // Blocking is availability management, so it is offered on the row of
  // whoever's day this is — your own, or anyone's if you manage the barn.
  canBlock = false,
  onBlockSlot,
  onUnblock,
  // Changing a booked lesson's shape for one date. Same permission as
  // blocking: it is this trainer's day either way.
  onConvert,
}) {
  const slotsQ = useQuery({
    queryKey: ['slots', date],
    queryFn: () => api.get(`/slots?from=${date}&to=${date}`),
    retry: false,
  });

  const dayQ = useQuery({
    queryKey: ['availability-day', date, primaryTrainerId],
    queryFn: () => api.get(`/availability/day/${date}?trainerUserId=${primaryTrainerId}`),
    retry: false,
  });

  const all = slotsQ.data?.slots ?? [];
  // Every entry covering this date. An all-day one is the day's whole state; a
  // part-day one sits next to hours that still run, and both want saying.
  const entries = dayQ.data?.timeOff ?? [];
  const wholeDayOff = entries.find((e) => e.allDay) ?? null;
  const partials = entries.filter((e) => !e.allDay);
  const ridersAway = dayQ.data?.ridersAway ?? [];

  // In the order the chips show them, so the panel reads the same way as the row
  // above it — and with the focused trainer present whether or not they're in
  // `trainers`, so switching your own chip off never hides your own day.
  const roster = trainers.some((t) => t.userId === primaryTrainerId)
    ? trainers
    : [{ userId: primaryTrainerId, name: dayQ.data?.trainerName ?? 'You' }, ...trainers];

  const sections = roster.map((t) => ({
    ...t,
    dot: t.userId === primaryTrainerId ? null : dotFor(t.userId),
    slots: all
      .filter((s) => s.trainerUserId === t.userId)
      .sort((a, b) => a.startTime.localeCompare(b.startTime)),
  }));

  // Green border and a wash of the same green, matching the ring on the cell it
  // dropped out of. A neutral gray panel floated free of the day that opened it;
  // sharing a color is what ties the two together.
  return (
    <div className="rounded-lg border-2 border-hunter bg-hunter/5 p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="font-medium">{fmtDate(date, 'dayLong')}</h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close day"
          className="text-ink-500 hover:text-ink-900 text-2xl font-semibold leading-none px-2 -mt-1"
        >
          ×
        </button>
      </div>

      {slotsQ.isLoading ? (
        <div className="h-20 bg-white rounded animate-pulse" />
      ) : (
        <div className="space-y-4">
          {proposedSlots > 0 && (
            <p className="text-xs text-ink-500 rounded-md border border-dashed border-ink-300 px-2 py-1.5">
              The calendar also shows <strong>{proposedSlots}</strong>{' '}
              {proposedSlots === 1 ? 'slot' : 'slots'} here from the pattern you&apos;re building.
              They aren&apos;t saved yet, so they&apos;re not listed below.
            </p>
          )}

          {ridersAway.length > 0 && (
            // Worth saying before the slots rather than after: a trainer looking
            // at an empty Tuesday should know Ada is at college, not wonder.
            <p className="text-xs text-ink-500">
              <span className="font-medium">Away:</span>{' '}
              {ridersAway.map((r) => r.name).join(', ')}
            </p>
          )}

          {sections.map((t) => (
            <TrainerDay
              key={t.userId}
              trainer={t}
              isPrimary={t.userId === primaryTrainerId}
              isOff={t.userId === primaryTrainerId ? isOff || !!wholeDayOff : false}
              offReason={t.userId === primaryTrainerId ? wholeDayOff?.reason ?? null : null}
              partials={t.userId === primaryTrainerId ? partials : []}
              wholeDayOff={t.userId === primaryTrainerId ? wholeDayOff : null}
              // The rest of their day. Only the focused trainer's is fetched —
              // the day endpoint answers about one person, and the other rows
              // are here to say who else is free, not to account for their
              // hours.
              shifts={t.userId === primaryTrainerId ? (dayQ.data?.shifts ?? []) : []}
              overridden={t.userId === primaryTrainerId && !!dayQ.data?.overridden}
              // Only your own row is editable. Turning off somebody else's day
              // from inside a view of your own is a mistake waiting to happen,
              // and switching to them is one tap on their chip.
              onEdit={t.userId === primaryTrainerId ? onEditHours : null}
              onBlockSlot={canBlock && t.userId === primaryTrainerId ? onBlockSlot : null}
              onUnblock={canBlock && t.userId === primaryTrainerId ? onUnblock : null}
              onConvert={canBlock && t.userId === primaryTrainerId ? onConvert : null}
              busy={busy}
            />
          ))}
        </div>
      )}

    </div>
  );
}

function TrainerDay({
  trainer, isPrimary, isOff, offReason, partials = [], wholeDayOff = null,
  shifts = [], overridden, onEdit, onBlockSlot, onUnblock, onConvert, busy,
}) {
  const { slots } = trainer;
  const first = slots[0];
  const last = slots[slots.length - 1];

  // The old wording read as a decision somebody had made. Usually nobody has —
  // there is simply no pattern covering this day yet, and saying so points at
  // what to do about it.
  const summary = isOff
    ? offReason || 'Day off'
    : slots.length === 0
      ? 'No slots defined'
      : `${fmtTime(first.startTime)} – ${fmtTime(last.endTime)} · ${slots.length} ${
          slots.length === 1 ? 'slot' : 'slots'
        }${overridden ? ' · just this day' : ''}`;

  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <span
          className={
            'w-2.5 h-2.5 rounded-full shrink-0 ' + (isPrimary ? 'bg-hunter' : trainer.dot)
          }
        />
        <span className="font-medium text-sm">{trainer.name || 'Trainer'}</span>
        <span className="text-xs text-ink-500">{summary}</span>
        {onEdit && (
          <IconButton
            icon={PencilIcon}
            label="Change this day"
            onClick={onEdit}
            disabled={busy}
          />
        )}
      </div>

      {/* The focused trainer's whole day, drawn against the clock: the shift
          they are on, the hours they are away, and the teaching slots, in one
          column. Everybody else keeps the compact list — those rows are here
          to say who ELSE is free, and a rail each would bury that. */}
      {isPrimary ? (
        <DayTimeline
          shifts={shifts}
          timeOff={partials}
          slots={slots}
          allDayOff={wholeDayOff}
          onBlockSlot={onBlockSlot}
          onUnblock={onUnblock}
          onConvert={onConvert}
          busy={busy}
        />
      ) : (
        slots.length > 0 && (
        <ul className="rounded-md border border-ink-200 bg-white divide-y divide-ink-100 overflow-hidden">
          {slots.map((s) => (
            <li
              key={`${s.availabilityId}-${s.startTime}`}
              className="flex items-baseline gap-3 px-3 py-1.5 text-sm"
            >
              <span className="tabular-nums text-ink-500 w-20 shrink-0">
                {fmtTime(s.startTime)}
              </span>
              {s.riders.length === 0 ? (
                <span className="flex-1 min-w-0 flex items-baseline gap-2">
                  <span className="text-ink-300">
                    {/* An open slot still has a type if the block named one —
                        "Jump · Open" tells you what you'd be offering. */}
                    {s.lessonTypeName && (
                      <span className="font-medium text-ink-500">{s.lessonTypeName} · </span>
                    )}
                    Open{s.capacity > 1 ? ` · ${s.capacity} places` : ''}
                  </span>
                  {/* Only ever on an OPEN slot. Blocking over a booked ride
                      would strand it — the lesson stays in the database and
                      disappears off the calendar, so the rider turns up and the
                      trainer doesn't. The server refuses it too; this just
                      never offers it. */}
                  {onBlockSlot && (
                    // A pencil rather than the word "Block". The row above
                    // carries one for the day's hours, so the gesture is
                    // already learned here — and "Block" named the one thing
                    // the dialog does rather than the fact that this hour can
                    // be changed at all.
                    <span className="ml-auto shrink-0">
                      <IconButton
                        icon={PencilIcon}
                        label={`Change ${fmtTime(s.startTime)}`}
                        onClick={() => onBlockSlot(s)}
                        disabled={busy}
                      />
                    </span>
                  )}
                </span>
              ) : (
                <span className="min-w-0 flex-1">
                  {s.riders.map((r, i) => (
                    <span key={r.lessonId} className="block truncate">
                      {/* The lesson's own type, falling back to the one the slot
                          was published as. A slot offered as "Any" takes
                          whatever the trainer books into it, and it's the
                          booking that's true. */}
                      {(r.lessonTypeName || s.lessonTypeName) && (
                        <span className="font-semibold text-ink-800">
                          {r.lessonTypeName || s.lessonTypeName}{' · '}
                        </span>
                      )}
                      {r.name}
                      {r.horseName && <span className="text-ink-400"> · {r.horseName}</span>}
                      {i === s.riders.length - 1 && s.open > 0 && (
                        <span className="text-ink-300"> · {s.open} still open</span>
                      )}
                    </span>
                  ))}
                </span>
              )}
            </li>
          ))}
        </ul>
        )
      )}
    </div>
  );
}
